import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Checkbox, Grid, IconButton, MenuItem, Tooltip,
} from '@mui/material';
import AutoCompleteContext from 'components/CustomInputsInFormikContext/AutoComplete';
import NumberFormatContext from 'components/CustomInputsInFormikContext/NumberFormat';
import SelectFieldContext from 'components/CustomInputsInFormikContext/Select';
import { formatValueInBrazilianCurrency, labelField } from 'utils';
import { useFormikContext } from 'formik';
import { ReactComponent as Trash } from 'images/icons/trash.svg';
import AddIcon from '@mui/icons-material/Add';
import CustomApportionmentModal from './CustomApportionmentModal';
import Error from 'components/Snack/Error';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { convertValueToInteger } from 'components/Bill/helpers';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

function InputGroup({
  index,
  accountCenters,
  remainingTotalDisplayError,
  mappedPonds,
  billValue,
  onRemove,
  onAdd,
}) {
  const [openModal, setOpenModal] = useState(false);
  const {
    getFieldProps, setFieldValue,
  } = useFormikContext();
  const { value: movements } = getFieldProps('movements');

  const movement = movements[index];

  const error = Error();

  const checkType = useCallback((type) => movement.type?.includes(type), [movement.type]);

  const updateSelectedPonds = (pondsByModal) => {
    setFieldValue(`movements.${index}.ponds`, pondsByModal);
  };

  // preencher o valor de rateio conforme o valor inserido em dados do pagamento
  useEffect(() => {
    if ((billValue && !movements[0].value) || movements.length === 1) {
      setFieldValue(`movements.${0}.value`, billValue);
    }
  }, [billValue, movements, setFieldValue]);

  const apportionmentValueDividedBetweenPonds = useCallback((option, key) => {
    if (!movement.value || movement.value === 0) {
      return 0;
    } if (checkType('Proporcional')) {
      return movement.value / movement.ponds.length;
    }
    return option[key];
  }, [checkType, movement.ponds.length, movement.value]);

  const handleChangePonds = (values) => {
    if (checkType('Proporcional')) {
      const mapped = values?.map((pond) => ({
        ...pond,
        value: parseFloat(movement.value / values.length),
      }));

      setFieldValue(`movements.${index}.ponds`, mapped);
    } else if (checkType('Personalizado')) {
      return null;
    } else {
      setFieldValue(`movements.${index}.ponds`, values);
    }
    return null;
  };

  const handleApportionmentValue = useCallback((value) => {
    const updatedApportionment = parseFloat(value);
    const updatedApportionments = [...movements];
    updatedApportionments[index] = {
      ...movement,
      value: updatedApportionment,
    };

    const currentTotal = updatedApportionments.reduce(
      (acc, curr) => acc + curr.value,
      0,
    );

    if (billValue && (convertValueToInteger(currentTotal) > convertValueToInteger(billValue))) {
      error('Erro ao colocar valor do rateio acima do valor total da conta!');
      setFieldValue(`movements.${index}.value`, movements[index].value);
      return;
    }

    setFieldValue(`movements[${index}].value`, updatedApportionment);
  }, [billValue, error, index, movement, movements, setFieldValue]);

  return (
    <Box display="flex" width="100%" gap={2}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} lg={3}>
          <AutoCompleteContext
            label={labelField('Centro de Custo')}
            name={`movements.${index}.accountCenter`}
            options={accountCenters}
            getOptionLabel={(option) => `${option.cod} ${option.description}`}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <NumberFormatContext
            fieldSize={20}
            label="Valor do rateio"
            name={`movements.${index}.value`}
            prefix="R$ "
            decimalScale={2}
            onValueChangeCallback={handleApportionmentValue}
            onBlurCallback={() => {
              if (checkType('Personalizado') && movements.length > 1) {
                setFieldValue(`movements.${index}.ponds`, []);
              }
            }}
            customError={Boolean(remainingTotalDisplayError)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <SelectFieldContext
            label="Tipo de rateio por viveiro"
            name={`movements.${index}.type`}
          >
            <MenuItem key={1} value="Proporcional">Proporcional</MenuItem>
            <MenuItem
              disabled={!movement.accountCenter}
              key={2}
              value="Personalizado"
              onClick={() => {
                setOpenModal(true);
                if (checkType('Proporcional')) {
                  setFieldValue(`movements.${index}.ponds`, []);
                }
              }}
            >
              Personalizado
            </MenuItem>
            <MenuItem
              key={3}
              value="Sem rateio por viveiro"
              onClick={() => {
                setFieldValue(`movements.${index}.ponds`, []);
              }}
            >
              Sem rateio por viveiro
            </MenuItem>
          </SelectFieldContext>
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <AutoCompleteContext
            disabled={checkType('Sem rateio por viveiro')}
            multiple
            disableCloseOnSelect
            label={labelField('Viveiros')}
            id={`movements.${index}.ponds`}
            name={`movements.${index}.ponds`}
            options={mappedPonds}
            getOptionLabel={(option) => `${option.pond?.description} - ${formatValueInBrazilianCurrency(apportionmentValueDividedBetweenPonds(option, 'value'))}`}
            renderOptionLabel={(option) => option.pond?.description}
            onChange={handleChangePonds}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option.pond?.description}
              </li>
            )}
          />
        </Grid>
      </Grid>
      <Grid
        item
        xs="auto"
        display="flex"
        alignItems="center"
        justifyContent="flex-end"
      >
        {index > 0 && (
          <Tooltip title="Remover">
            <IconButton sx={{ bgcolor: '#FFDAD6' }} size="large" onClick={onRemove}>
              <Trash style={{ width: 14, height: 14, color: '#B3261E' }} />
            </IconButton>
          </Tooltip>
        )}

        {index === 0 && (
          <Tooltip title="Adicionar">
            <IconButton sx={{ bgcolor: '#DBE5DE' }} onClick={onAdd}>
              <AddIcon sx={{ color: '#006E2D' }} />
            </IconButton>
          </Tooltip>
        )}
      </Grid>
      {openModal && (
        <CustomApportionmentModal
          open={openModal}
          setOpen={setOpenModal}
          apportionmentData={movement}
          mappedPonds={mappedPonds}
          callback={updateSelectedPonds}
        />
      )}
    </Box>
  );
}

export default InputGroup;
